import { S } from '../state/store.js';
import { emit } from '../state/bus.js';
import { openClip } from '../unwrap/decode.js';

/* THE FILMED VIDEO behind an on-site render.

   The on-site mockup (export/onsite.js) puts the deliverable on a PHOTOGRAPH of the
   building. With a video of the building instead, the same render is made over every
   frame of it: the building moves, the screen stays where it was marked, and the
   illusion plays on it. The corners are marked once, on a still, so the camera has to
   have stayed still — the same condition as the Unwrap tool, and the reason a video
   fits in at all: the photo's four marks are the video's four marks.

   WHAT THE PHOTO IS. A frame of this video, taken when it was loaded (ui/frame-picker.js),
   or any photo from the same camera at the same framing, with the video chosen afterwards
   in the Export dialog. Either way the photo may have been CROPPED (the builder's crop
   step), and then the video has to be cropped the same way; a rotated or straightened
   photo has new pixels baked in that a video cannot follow, so that is refused with a
   reason rather than rendered subtly wrong.

   SESSION ONLY, like the Unwrap tool's clip and unlike the photo: a filmed video is
   hundreds of megabytes, and nothing in a saved project could hold it. It is dropped when a
   project is opened (services/projects.js applyDoc), and the Export dialog offers to choose
   it again. */

let cur = null;                     // { clip }

const FULL = { x0: 0, y0: 0, x1: 1, y1: 1 };

/**
 * Why this video cannot stand behind this photo, or null when it can. Re-asked every time it
 * is used, so a photo that is re-cropped or replaced after the video was chosen is caught
 * instead of rendered against the wrong frame.
 */
export function siteClipProblem(clip, photo) {
  if (!photo) return 'There is no reference photo yet.';
  if (photo.rotated)
    return 'The photo was rotated or straightened, which a video cannot follow. Load the photo again without rotating it.';
  const r = photo.rect || FULL;
  const shape = ((r.x1 - r.x0) * clip.w) / ((r.y1 - r.y0) * clip.h);
  const want = photo.w / photo.h;
  if (Math.abs(shape - want) / want > 0.015)
    return `That video is a different shape from the photo (the video is ${clip.w} × ${clip.h}, the photo ${photo.w} × ${photo.h}). `
      + 'Use a video from the same camera, framed the same way.';
  return null;
}

/** What is loaded, for the Export dialog; null when nothing is. */
export function siteClipInfo() {
  if (!cur) return null;
  const c = cur.clip;
  return { name: c.name, duration: c.duration, fps: c.fps, w: c.w, h: c.h, problem: siteClipProblem(c, S.photo) };
}

/** True when a video is loaded AND fits the photo as it is now. */
export const siteClipReady = () => !!cur && !siteClipProblem(cur.clip, S.photo);

/** Use an already-opened clip (the one the frame was taken from). Throws with the reason if it does not fit. */
export function adoptSiteClip(clip) {
  const problem = siteClipProblem(clip, S.photo);
  if (problem) throw new Error(problem);
  cur?.clip.close();
  cur = { clip };
  emit('siteclip:changed');
}

/** Open a video file and use it. Throws with a sentence for the person. */
export async function chooseSiteClip(file) {
  const clip = await openClip(file);
  try { adoptSiteClip(clip); }
  catch (e) { clip.close(); throw e; }
}

export function clearSiteClip() {
  if (!cur) return;
  cur.clip.close();
  cur = null;
  emit('siteclip:changed');
}

/** Where the photo's frame sits in the video's frame, in pixels of a decoded picture `w` x `h`. */
const cropOf = (photo, w, h) => {
  const r = photo?.rect || FULL;
  const sx = Math.round(r.x0 * w), sy = Math.round(r.y0 * h);
  return { sx, sy, sw: Math.max(2, Math.round((r.x1 - r.x0) * w)), sh: Math.max(2, Math.round((r.y1 - r.y0) * h)) };
};

/** The clip's time for an instant of the timeline: it loops under the animation. */
const loopTime = (t, dur) => (dur > 0 ? ((t % dur) + dur) % dur : 0);

/**
 * The video as it was at time `t` (looping), cut to the photo's framing: a canvas the shape of
 * the photo, for compositeOnSite to draw in its place. For the still export.
 */
export async function siteFrameAt(t) {
  const { clip } = cur;
  const f = await clip.frameAt(loopTime(t, clip.duration), clip.w);
  if (!f) throw new Error('That moment could not be read from the filmed video.');
  const { sx, sy, sw, sh } = cropOf(S.photo, f.canvas.width, f.canvas.height);
  const out = document.createElement('canvas');
  out.width = sw; out.height = sh;
  out.getContext('2d').drawImage(f.canvas, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}

/**
 * A source of backgrounds for a whole export. `instants` are the timeline's times, in the order
 * they will be asked for; next() then gives the video's frame for each, in turn. The video loops
 * when the animation is longer than it, and each loop is one pass of the batch decoder (which needs
 * its list up front, and decodes each packet once).
 */
export function createBackdrop(instants) {
  const { clip } = cur;
  const dur = clip.duration;
  const plan = instants.map(t => { const loop = Math.floor(t / Math.max(dur, 1e-6)); return { loop, t: loopTime(t, dur) }; });
  let i = 0, gen = null, genLoop = null, last = null;
  const out = document.createElement('canvas');
  let sized = false;

  const close = async () => { const g = gen; gen = null; try { await g?.return?.(); } catch (_) { /* closed */ } };

  return {
    async next() {
      const p = plan[i++];
      if (!p) return last || out;
      if (p.loop !== genLoop) {
        await close();
        const times = [];
        for (let k = i - 1; k < plan.length && plan[k].loop === p.loop; k++) times.push(plan[k].t);
        gen = clip.framesAt(times, 1920);
        genLoop = p.loop;
      }
      const { value } = await gen.next();
      const src = value?.canvas;
      if (src) {
        const { sx, sy, sw, sh } = cropOf(S.photo, src.width, src.height);
        if (!sized || out.width !== sw || out.height !== sh) { out.width = sw; out.height = sh; sized = true; }
        out.getContext('2d').drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
        last = out;
      }
      return last || out;                  // past the end of the video: its last frame, rather than nothing
    },
    close
  };
}
